import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCalendarRedirectUri,
  getSecondaryCalendarInfo,
} from "../../../../api/appointments/calendar-management";
import Loader from "../../../../components/Loader";
import SectionItem from "../../../../components/section/section-item/SectionItem";
import {
  setCalendarFound,
  updateCalendarInfo,
  updateIsSameAsOpeningHours,
} from "../../../../redux/actions/CalendarManagementActions";
import { getOrganizationFeatureFlag } from "../../../../services-logic/base-logic";
import ConnectToCalendarButtons from "./ConnectToCalendarButtons";
import ConnectedToCalendarButtons from "./ConnectedToCalendarButtons";

export const TEMP_CALENDAR_ID = "temp-calendar-id";

const CalendarSection = () => {
  const dispatch = useDispatch();
  const { organizationId, accountId, workspaceId } = useSelector((state) => state.info);
  const { productName } = useSelector((state) => state.info.organization);
  const [isConnecting, setIsConnecting] = useState(false);

  const { calendarInfo, updatedCalendarInfo } = useSelector((state) => state.calendarManagement);
  const currentProviderRef = useRef("");
  const currentAliasNameRef = useRef("");

  const isConnected = updatedCalendarInfo && updatedCalendarInfo.id;
  const credentialsKey = getOrganizationFeatureFlag("SR-SNIPPET", "credentialsKey");

  // connecting type can be new/update to know if we are connecting a new calendar or updating the existing one
  const [connectingType, setConnectingType] = useState("");

  const generateCalendarAliasName = () => {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  };

  const onConnectToCalendar = async (provider, connectingType) => {
    setIsConnecting(true);
    setConnectingType(connectingType);
    const aliasName = generateCalendarAliasName();
    const redirectUri = await getCalendarRedirectUri({
      aliasName,
      provider,
      credentialsKey,
      organizationId,
      accountId,
    });
    currentProviderRef.current = provider;
    currentAliasNameRef.current = aliasName;

    const oauthWindow = window.open(redirectUri, "OAuthWindow", "width=500,height=800");

    if (!oauthWindow) {
      // Handle the case where the window couldn't be opened
      alert("Popup blocked. Please allow popups and try again.");
      setIsConnecting(false);
      return;
    }

    const checkWindowClosed = setInterval(async () => {
      if (oauthWindow.closed) {
        clearInterval(checkWindowClosed);
        setIsConnecting(false);
      }
    }, 1000);
  };

  const getCalendarInfo = async (updatedCalendarInfoInternal) => {
    const aliasName = updatedCalendarInfoInternal.aliasName;
    const calendarProvider = updatedCalendarInfoInternal.provider;
    var calendarName = `[${productName}] #${workspaceId} (${accountId},${organizationId})`;

    var request = {
      aliasName,
      calendarName,
      calendarProvider,
      credentialsKey,
      organizationId,
      accountId,
    };

    if (updatedCalendarInfoInternal?.id && updatedCalendarInfoInternal?.id !== TEMP_CALENDAR_ID) {
      request.calendarId = updatedCalendarInfoInternal.id;
    }

    const secondaryCalendarInformation = await getSecondaryCalendarInfo(request);

    if (secondaryCalendarInformation?.error) {
      updatedCalendarInfoInternal.id = null;
      dispatch(setCalendarFound(false));
    } else {
      const calendarFound =
        secondaryCalendarInformation && secondaryCalendarInformation.calendarFound;
      if (calendarFound) {
        updatedCalendarInfoInternal.id = secondaryCalendarInformation.calendarId;
      }
      dispatch(setCalendarFound(calendarFound));
    }
    dispatch(updateCalendarInfo(updatedCalendarInfoInternal));
  };

  useEffect(() => {
    if (updatedCalendarInfo?.id && updatedCalendarInfo?.id !== TEMP_CALENDAR_ID) {
      getCalendarInfo(updatedCalendarInfo);
      currentProviderRef.current = updatedCalendarInfo.provider;
      currentAliasNameRef.current = updatedCalendarInfo.aliasName;
    }
  }, [calendarInfo]);

  useEffect(() => {
    const handleCalendarConnection = async (event) => {
      if (event.data === "auth-successful") {
        const updatedCalendarInfoInternal = {
          provider: currentProviderRef.current,
          aliasName: currentAliasNameRef.current,
          id: TEMP_CALENDAR_ID,
        };

        if (connectingType === "new") {
          dispatch(updateIsSameAsOpeningHours(true));
        }
        await getCalendarInfo(updatedCalendarInfoInternal);
        setConnectingType("");
        setIsConnecting(false);
      }
    };

    window.addEventListener("message", handleCalendarConnection);

    return () => {
      window.removeEventListener("message", handleCalendarConnection);
    };
  }, [connectingType]);

  const calendarContent = (
    <>
      {isConnecting && (
        <div className="flex w-[150px]">
          <span className="text-[10px] md:text-[12px] lg:text-[14px] font-semibold">
            Connecting
          </span>
          <Loader size="24" border="border-4" />
        </div>
      )}
      {!isConnecting && !isConnected && !updatedCalendarInfo?.id && (
        <ConnectToCalendarButtons onConnectToCalendar={onConnectToCalendar} />
      )}
      {!isConnecting && isConnected && (
        <ConnectedToCalendarButtons onConnectToCalendar={onConnectToCalendar} />
      )}
    </>
  );

  return (
    <SectionItem
      title="Calendar"
      subtitle={`Connect your calendar where appointments will be set and managed. Connecting your calendar will create a new, secondary one called '[${productName}]'. Any ${productName} booked appointments will appear there. We will check your availability on both calendars, before booking anything.`}
      content={calendarContent}
    />
  );
};

export default CalendarSection;
